import {
  ref,
  uploadBytesResumable,
  UploadTask,
} from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { storage, functions } from '../config';

// Guests never read Storage objects directly — the backend mints short-lived
// signed URLs (see wedding-upload-be galleryMedia.ts + storage.rules), so a
// stranger who learns a siteId cannot enumerate/bulk-download a gallery.
const listGalleryMediaCallable = httpsCallable<
  { siteId: string; folder?: string; pageToken?: string; maxResults?: number },
  { items: GalleryFile[]; nextPageToken: string | null }
>(functions, 'listGalleryMedia');

const getGalleryFileUrlCallable = httpsCallable<
  { siteId: string; path: string },
  { url: string }
>(functions, 'getGalleryFileUrl');

export interface UploadProgress {
  progress: number; // 0-100
  bytesTransferred: number;
  totalBytes: number;
}

export interface UploadResult {
  success: boolean;
  downloadURL?: string;
  error?: string;
  fileName?: string;
  cancelled?: boolean;
}

export interface UploadTaskHandle {
  uploadTask?: UploadTask;
  result: Promise<UploadResult>;
}

export interface GalleryFile {
  name: string;
  fullPath: string;
  downloadUrl: string;
  contentType?: string;
  size?: number;
  timeCreated?: string;
  updated?: string;
}

export interface ListGalleryOptions {
  folder?: string;
  pageToken?: string | null;
  maxResults?: number;
}

export interface GalleryListResult {
  items: GalleryFile[];
  nextPageToken: string | null;
}

/**
 * Uploads a file to Firebase Storage
 * @param file - The file to upload
 * @param folder - Optional folder path in storage (e.g., 'wedding-photos')
 * @param onProgress - Callback for upload progress updates
 * @returns Promise with upload result
 */
export const uploadFileToFirebase = (
  file: File,
  folder: string = 'uploads',
  onProgress?: (progress: UploadProgress) => void
): UploadTaskHandle => {
  let uploadTask: UploadTask | undefined;

  const result = new Promise<UploadResult>((resolve) => {
    try {
      // Create a unique filename to avoid collisions
      const timestamp = Date.now();
      const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const fileName = `${timestamp}_${sanitizedName}`;

      // Create storage reference
      const storageRef = ref(storage, `${folder}/${fileName}`);

      // Create upload task
      const task = uploadBytesResumable(storageRef, file);
      uploadTask = task;

      // Monitor upload progress
      task.on(
        'state_changed',
        (snapshot) => {
          // Calculate progress percentage
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;

          // Call progress callback if provided
          if (onProgress) {
            onProgress({
              progress: Math.round(progress),
              bytesTransferred: snapshot.bytesTransferred,
              totalBytes: snapshot.totalBytes
            });
          }
        },
        (error: any) => {
          // Handle upload error or cancellation
          console.error('Upload error:', error);
          resolve({
            success: false,
            error: error.message || 'Upload failed',
            fileName: file.name,
            cancelled: error?.code === 'storage/canceled'
          });
        },
        () => {
          // Upload completed successfully. Storage objects are not publicly
          // readable, so we hand back a local object URL for immediate
          // preview; the gallery itself refreshes from signed URLs.
          resolve({
            success: true,
            downloadURL: URL.createObjectURL(file),
            fileName: file.name
          });
        }
      );
    } catch (error: any) {
      resolve({
        success: false,
        error: error.message || 'Failed to start upload',
        fileName: file.name
      });
    }
  });

  return {
    uploadTask,
    result
  };
};

export const listGalleryFiles = async (
  options: ListGalleryOptions = {}
): Promise<GalleryListResult> => {
  const {
    folder = 'wedding-photos',
    pageToken,
    maxResults = 20,
  } = options;

  // `folder` is `sites/{siteId}/{kind}` (see galleryEpic). The backend lists
  // that subfolder and returns each object with a short-lived signed URL, so
  // no direct Storage read/list happens from the browser.
  const parts = folder.split('/');
  const siteId = parts[1] ?? '';
  const kind = parts[2] ?? 'thumbnail';

  const { data } = await listGalleryMediaCallable({
    siteId,
    folder: kind,
    maxResults,
    ...(pageToken ? { pageToken } : {}),
  });

  return {
    items: data.items,
    nextPageToken: data.nextPageToken ?? null,
  };
};

export const getOriginalFileUrl = async (
  thumbnailName: string,
  siteId: string
): Promise<{ url: string; type: 'image' | 'video'; name: string } | null> => {
  try {
    // Extract the middle part between @ symbols: xxx@ccc@rrr -> ccc
    const atMatch = thumbnailName.match(/@(.+)@/);
    
    if (!atMatch) {
      console.warn(`Invalid thumbnail name format (no @ delimiters): ${thumbnailName}`);
      return null;
    }

    const middlePart = atMatch[1]; // The ccc part
    
    // Check if there are curly braces {}
    const curlyMatch = middlePart.match(/\{(VIDEO|HEIC)\}/);

    const prefix = `sites/${siteId}`;
    let path: string;
    let type: 'image' | 'video';
    let name: string;

    if (!curlyMatch) {
      // No {} - use whole middlePart as filename, fetch from "original"
      path = `${prefix}/original/${middlePart}`;
      type = 'image';
      name = middlePart;
    } else {
      // Has {} - extract type
      const typeStr = curlyMatch[1]; // VIDEO or HEIC
      
      if (typeStr === 'HEIC') {
        // HEIC -> use whole middlePart, fetch from "converted" folder
        path = `${prefix}/converted/${middlePart}`;
        type = 'image';
        // The converted file is a JPEG named {HEIC}_[<original>].jpeg.
        // Download it under the original name with a .jpg extension.
        const bracketMatch = middlePart.match(/\[(.+)\]/);
        name = (bracketMatch ? bracketMatch[1] : middlePart).replace(/\.(heic|heif)$/i, '.jpg');
      } else {
        // VIDEO -> extract new name from [], fetch from "original" folder
        const bracketMatch = middlePart.match(/\[(.+)\]/);
        
        if (!bracketMatch) {
          console.warn(`Invalid thumbnail name format (no [] for new name): ${thumbnailName}`);
          return null;
        }

        const newName = bracketMatch[1];
        path = `${prefix}/original/${newName}`;
        type = 'video';
        name = newName;
      }
    }


    const { data } = await getGalleryFileUrlCallable({ siteId, path });

    return { url: data.url, type, name };
  } catch (error) {
    console.error('Failed to get original file URL:', error);
    return null;
  }
};